import clsx from 'clsx'
import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import type { CornerBannerResult } from '../data/CornerBannerFragment'
import { contemberLinkToHref } from '../utilities/contemberLinkToHref'
import { Button } from './Button'
import s from './CornerBanner.module.sass'
import { ContemberImage } from './ContemberImage'
import { Icon } from './Icon'

export type CornerBannerProps = {
	cornerBanner: CornerBannerResult
	/** Page has no sticky bar at the bottom, so the banner can sit lower. */
	withoutStickyBar?: boolean
}

const DISMISS_STORAGE_KEY = 'cornerBannerDismissed'

export const CornerBanner: FunctionComponent<CornerBannerProps> = ({ cornerBanner, withoutStickyBar = false }) => {
	const [hasScrolled, setHasScrolled] = useState(false)
	// Start dismissed so nothing flashes before we read the stored state on the client.
	const [isDismissed, setIsDismissed] = useState(true)

	useEffect(() => {
		setIsDismissed(window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === 'true')
	}, [])

	useEffect(() => {
		const handleScroll = () => {
			if (window.scrollY > 0) {
				setHasScrolled(true)
				window.removeEventListener('scroll', handleScroll)
			}
		}
		window.addEventListener('scroll', handleScroll, { passive: true })
		handleScroll()
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	const handleClose = () => {
		setIsDismissed(true)
		window.sessionStorage.setItem(DISMISS_STORAGE_KEY, 'true')
	}

	if (!cornerBanner.isActive) {
		return null
	}

	const isVisible = hasScrolled && !isDismissed
	const hasButton = Boolean(
		cornerBanner.link &&
			(cornerBanner.link.internalLink || cornerBanner.link.externalLink || cornerBanner.link.anchorLink),
	)

	return (
		<div
			className={clsx(s.Wrapper, isVisible && s.is_visible, withoutStickyBar && s.is_withoutStickyBar)}
			aria-hidden={!isVisible}
		>
			<div className={s.Banner}>
				<button type="button" className={s.Close} onClick={handleClose} aria-label="Zavřít">
					<Icon name="close" />
				</button>

				{cornerBanner.logo && (
					<div className={s.Logo}>
						<ContemberImage image={cornerBanner.logo} />
					</div>
				)}

				{cornerBanner.title && <p className={s.Title}>{cornerBanner.title}</p>}
				{cornerBanner.text && <p className={s.Text}>{cornerBanner.text}</p>}

				{hasButton && cornerBanner.link && (
					<div className={s.Button}>
						<Button type="link" href={contemberLinkToHref(cornerBanner.link)} variant="white">
							{cornerBanner.link.title}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
